import { useState, useEffect } from "react";
import { t, font } from "@realtyflows/ui";
import { getMarketContext } from "../api";

/**
 * MarketContext — V4 Feature 4.4
 *
 * Shows aggregate MLS stats for a client's target area (city or zip):
 *   - Median list / sold prices
 *   - Average days on market
 *   - Recent sales count
 *   - Active listings count
 *   - Avg $/sqft
 *
 * Hides itself when no `area` prop is passed (client has no target area set).
 *
 * Props:
 *   area  — client.location (city or "city, state" or zip)
 */
export default function MarketContext({ area }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!area) { setLoading(false); return; }
    // Try city first, fall back to zip if input looks numeric
    const isZip = /^\d{5}$/.test(area.trim());
    const params = isZip ? { zip: area.trim() } : { city: area.split(",")[0].trim() };
    getMarketContext(params)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [area]);

  if (loading || !data) return null;

  const fmtMoney = (cents) => cents == null ? "—" : `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  return (
    <div style={{ padding: 14, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
          Market Context · {data.area}
        </div>
        <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>
          MLS data · {new Date(data.asOf).toLocaleDateString()}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        <Stat label="Median List" value={fmtMoney(data.medianListPrice)} />
        <Stat label="Median Sold" value={fmtMoney(data.medianSoldPrice)} />
        <Stat label="Avg DOM" value={`${data.avgDom}d`} />
        <Stat label="Sold (30d)" value={String(data.soldLast30)} />
        <Stat label="Active Listings" value={String(data.activeListings)} />
        <Stat label="Avg $/sqft" value={fmtMoney(data.pricePerSqft)} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: t.surfaceWarm, borderRadius: 6, padding: "8px 10px" }}>
      <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.mono, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.mono, marginTop: 2 }}>{value}</div>
    </div>
  );
}
